import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import MapView from '@/components/MapView';
import { Truck, Package, Users, TrendingUp, MapPin, AlertCircle, Inbox, ArrowRight, Sparkles, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, loadsRes, driversRes, pipelineRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/loads?limit=50`),
        axios.get(`${API}/drivers?limit=50`),
        axios.get(`${API}/pipeline/stats`).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setLoads(loadsRes.data);
      setDrivers(driversRes.data);
      setPipelineStats(pipelineRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color }) => (
    <Card className="glass-card hover:border-primary/30 transition-all duration-300" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">{title}</p>
            <h3 className="text-3xl font-black font-heading text-foreground">{value}</h3>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg bg-${color}-500/15`}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center mt-4 text-xs text-emerald-500">
            <TrendingUp className="w-3 h-3 mr-1" />
            <span>{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const RecentActivity = ({ loads }) => (
    <Card className="glass-card" data-testid="recent-activity-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading">Recent Loads</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {loads.slice(0, 8).map((load) => (
            <div
              key={load.id}
              className="flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => navigate('/dispatch')}
              data-testid={`load-item-${load.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <p className="text-sm font-medium text-foreground">{load.pickup_address.substring(0, 40)}...</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {load.commodity} • {load.weight} lbs • ${load.rate}
                </p>
              </div>
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md ${
                load.status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' :
                load.status === 'in_transit' ? 'bg-amber-500/15 text-amber-500' :
                load.status === 'assigned' ? 'bg-blue-500/15 text-blue-500' :
                'bg-red-500/15 text-red-500'
              }`}>
                {load.status}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const DriverStatus = ({ drivers }) => (
    <Card className="glass-card" data-testid="driver-status-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading">Driver Status</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {drivers.slice(0, 6).map((driver) => (
            <div
              key={driver.id}
              className="flex items-center justify-between p-3 rounded-lg bg-background/50"
              data-testid={`driver-item-${driver.id}`}
            >
              <div>
                <p className="text-sm font-medium text-foreground">{driver.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{driver.phone}</p>
              </div>
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md ${
                driver.status === 'available' ? 'bg-emerald-500/15 text-emerald-500' :
                'bg-amber-500/15 text-amber-500'
              }`}>
                {driver.status}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="dashboard-container">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-2">Real-time dispatch operations overview</p>
          </div>
          <Button onClick={fetchData} className="glow-button" data-testid="refresh-button">
            Refresh Data
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Package}
            title="Active Loads"
            value={stats?.loads?.pending || 0}
            subtitle={`${stats?.loads?.in_transit || 0} in transit`}
            color="#3B82F6"
          />
          <StatCard
            icon={Truck}
            title="Available Trucks"
            value={stats?.trucks?.available || 0}
            subtitle={`${stats?.trucks?.total || 0} total fleet`}
            color="#10B981"
          />
          <StatCard
            icon={Users}
            title="Available Drivers"
            value={stats?.drivers?.available || 0}
            subtitle={`${stats?.drivers?.utilization_rate || 0}% utilized`}
            color="#F59E0B"
          />
          <StatCard
            icon={TrendingUp}
            title="Total Revenue"
            value={`$${(stats?.revenue?.total_revenue || 0).toLocaleString()}`}
            subtitle={`$${(stats?.revenue?.revenue_per_mile || 0).toFixed(2)}/mile`}
            trend="+12% from last period"
            color="#10B981"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="glass-card h-[500px]" data-testid="map-view-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Live Fleet Map
                </CardTitle>
              </CardHeader>
              <CardContent className="h-[400px]">
                <MapView loads={loads} drivers={drivers} />
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <RecentActivity loads={loads} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DriverStatus drivers={drivers} />
          
          {/* Pipeline Widget - NEW */}
          {pipelineStats && (
            <Card className="glass-card border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-purple-500/10" data-testid="pipeline-widget">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl font-heading flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-blue-400" />
                  Load Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-400">{pipelineStats.inbox?.new || 0}</p>
                    <p className="text-xs text-slate-400">New Emails</p>
                  </div>
                  <div className="p-3 bg-orange-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-orange-400">{pipelineStats.opportunities?.open || 0}</p>
                    <p className="text-xs text-slate-400">Open Opps</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-400">{pipelineStats.opportunities?.assigned || 0}</p>
                    <p className="text-xs text-slate-400">Booked</p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
                  <span>Email</span>
                  <ArrowRight className="w-4 h-4" />
                  <span>Opportunity</span>
                  <ArrowRight className="w-4 h-4" />
                  <span>Driver</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-green-400">💰 Profit</span>
                </div>
                <Button
                  onClick={() => navigate('/admin/pipeline')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Open Pipeline
                </Button>
              </CardContent>
            </Card>
          )}
          
          <Card className="glass-card" data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-xl font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => navigate('/loads')}
                className="w-full justify-start h-12 bg-primary hover:bg-primary/90"
                data-testid="create-load-button"
              >
                <Package className="w-5 h-5 mr-2" />
                Create New Load
              </Button>
              <Button
                onClick={() => navigate('/fleet')}
                className="w-full justify-start h-12 bg-secondary hover:bg-secondary/80"
                data-testid="add-driver-button"
              >
                <Users className="w-5 h-5 mr-2" />
                Add Driver/Truck
              </Button>
              <Button
                onClick={() => navigate('/dispatch')}
                className="w-full justify-start h-12 bg-secondary hover:bg-secondary/80"
                data-testid="dispatch-loads-button"
              >
                <Truck className="w-5 h-5 mr-2" />
                Dispatch Loads
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="glass-card bg-amber-500/10 border-amber-500/20" data-testid="api-key-notice">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-500">Google Maps API Setup Required</p>
                <p className="text-xs text-amber-500/80 mt-1">
                  To enable full map features, add your Google Maps API key to the backend .env file (GOOGLE_MAPS_API_KEY). 
                  <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                    Get your API key here
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;